/**
 * PixiJS Manager - coin sprite + particle effects
 * PixiJS layer doesn't know about game logic
 */
import * as PIXI from 'pixi.js';
import { formatNumber } from '../game/utils.js';

const DESIGN_W = 360;
const DESIGN_H = 640;

export interface PixiManager {
  init(container: HTMLElement): Promise<void>;
  spawnParticle(x: number, y: number, val: number, isCrit?: boolean, combo?: number): void;
  showComboText(combo: number): void;
  bounceCoin(): void;
  updateGlow(cps: number): void;
  getCoinCenter(): { x: number; y: number; r: number };
  resize(): void;
  destroy(): void;
}

export function createPixiManager(onTapCallback: (x: number, y: number) => void): PixiManager {
  let app: PIXI.Application;
  let dogeContainer: PIXI.Container;
  let dogeSprite: PIXI.Sprite;
  let dogeGlow: PIXI.Graphics;
  let particleContainer: PIXI.Container;
  let scale = 1;

  function calcScale() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    scale = Math.min(w / DESIGN_W, h / DESIGN_H);
  }

  function positionDoge() {
    // Place coin at ~35% from top of viewport (between HUD and tab bar)
    const hud = 80; // HUD height approx
    const tabBar = 60; // tab bar height approx
    const available = window.innerHeight - hud - tabBar;
    const coinY = hud + available * 0.22;
    dogeContainer.position.set(window.innerWidth / 2, coinY);
  }

  function drawGlow(r: number, color: number, alpha: number) {
    dogeGlow.clear();
    dogeGlow.beginFill(color, alpha);
    dogeGlow.drawCircle(0, 0, r * 1.4);
    dogeGlow.endFill();
    dogeGlow.beginFill(color, alpha * 0.5);
    dogeGlow.drawCircle(0, 0, r * 1.7);
    dogeGlow.endFill();
  }

  return {
    async init(container: HTMLElement) {
      try {
        app = new PIXI.Application({
          width: window.innerWidth,
          height: window.innerHeight,
          backgroundAlpha: 0,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          antialias: true,
        });
      } catch {
        // WebGL auto-detect failed — fallback to canvas renderer
        app = new PIXI.Application({
          width: window.innerWidth,
          height: window.innerHeight,
          backgroundAlpha: 0,
          resolution: window.devicePixelRatio || 1,
          autoDensity: true,
          antialias: true,
          forceCanvas: true,
        });
      }
      container.appendChild(app.view as HTMLCanvasElement);
      calcScale();

      const stage = app.stage;
      stage.eventMode = 'static';
      stage.hitArea = new PIXI.Rectangle(0, 0, window.innerWidth, window.innerHeight);

      dogeContainer = new PIXI.Container();
      dogeContainer.eventMode = 'static';
      positionDoge();
      stage.addChild(dogeContainer);

      // Particles on top of coin
      particleContainer = new PIXI.Container();
      particleContainer.eventMode = 'none';
      stage.addChild(particleContainer);

      // Glow effect
      const r = 70 * scale;
      dogeGlow = new PIXI.Graphics();
      dogeGlow.eventMode = 'none';
      drawGlow(r, 0xf0b90b, 0.12);
      dogeContainer.addChild(dogeGlow);

      // Coin sprite
      const coinTex = PIXI.Texture.from('/images/coin-click.png');
      dogeSprite = new PIXI.Sprite(coinTex);
      dogeSprite.anchor.set(0.5);
      const coinSize = r * 2;
      dogeSprite.width = coinSize;
      dogeSprite.height = coinSize;
      dogeContainer.addChild(dogeSprite);

      dogeSprite.eventMode = 'static';
      dogeSprite.cursor = 'pointer';
      dogeSprite.hitArea = new PIXI.Circle(0, 0, r * 1.8);
      dogeSprite.on('pointerdown', (e: PIXI.FederatedPointerEvent) => {
        onTapCallback(e.global.x, e.global.y);
      });
    },

    bounceCoin() {
      if (!app) return;
      // Scale bounce
      dogeContainer.scale.set(0.88);
      setTimeout(() => {
        dogeContainer.scale.set(1.05);
        setTimeout(() => dogeContainer.scale.set(1), 60);
      }, 60);

      // Flash ring effect
      const r = 70 * scale;
      const flash = new PIXI.Graphics();
      flash.lineStyle(3, 0xffd700, 0.8);
      flash.drawCircle(0, 0, r);
      flash.position.copyFrom(dogeContainer.position);
      app.stage.addChild(flash);
      let f = 0;
      const tick = () => {
        f++;
        const s = 1 + f * 0.04;
        flash.scale.set(s);
        flash.alpha = 1 - f / 15;
        if (f >= 15) {
          app.stage.removeChild(flash);
          flash.destroy();
          app.ticker.remove(tick);
        }
      };
      app.ticker.add(tick);
    },

    spawnParticle(x: number, y: number, val: number, isCrit = false, combo = 0) {
      if (!app) return;
      // Combo-based color enhancement
      let fill = isCrit ? 0xff4400 : 0xffffff;
      let stroke = isCrit ? 0x880000 : 0x1a1a2e;
      let fontSize = (isCrit ? 32 : 24);

      if (combo >= 50) {
        fill = 0x9D4EDD; stroke = 0x4A0080; fontSize = 34;
      } else if (combo >= 30) {
        fill = 0xFF4400; stroke = 0x880000; fontSize = 30;
      } else if (combo >= 10) {
        fill = 0xF7931A; stroke = 0x7A4800; fontSize = 28;
      }

      const t = new PIXI.Text(`+${formatNumber(val)}`, {
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(fontSize * scale),
        fontWeight: '900',
        fill,
        stroke,
        strokeThickness: 4,
        dropShadow: true,
        dropShadowColor: 0x000000,
        dropShadowBlur: 4,
        dropShadowDistance: 0,
      } as PIXI.TextStyle);
      t.anchor.set(0.5);
      t.position.set(x + (Math.random() - 0.5) * 60, y - 20);
      particleContainer.addChild(t);
      let f = 0;
      const tick = () => {
        f++;
        t.y -= 2.2;
        t.alpha = 1 - f / 35;
        if (f >= 35) {
          particleContainer.removeChild(t);
          t.destroy();
          app.ticker.remove(tick);
        }
      };
      app.ticker.add(tick);
    },

    showComboText(combo: number) {
      if (!app || combo < 5) return;

      // Mark previous combo text as dead (its ticker will clean it up)
      for (const c of Array.from(particleContainer.children)) {
        if ((c as any)._isCombo) (c as any)._isCombo = false;
      }

      // Determine color and size based on combo threshold
      let color = 0xFFD700;
      let size = 28;
      if (combo >= 50) { color = 0x9D4EDD; size = 38; }
      else if (combo >= 30) { color = 0xFF4400; size = 34; }
      else if (combo >= 10) { color = 0xF7931A; size = 32; }

      const t = new PIXI.Text(`x${combo}`, {
        fontFamily: 'Arial, sans-serif',
        fontSize: Math.round(size * scale),
        fontWeight: '900',
        fill: color,
        stroke: 0x000000,
        strokeThickness: 5,
        dropShadow: true,
        dropShadowColor: color,
        dropShadowBlur: 8,
        dropShadowDistance: 0,
      } as PIXI.TextStyle);
      (t as any)._isCombo = true;
      (t as any)._destroyed = false;
      t.anchor.set(0.5);
      t.position.set(dogeContainer.position.x, dogeContainer.position.y + 90 * scale);
      particleContainer.addChild(t);

      // Scale pop animation
      t.scale.set(1.3);
      let f = 0;
      const tick = () => {
        if ((t as any)._destroyed) { app.ticker.remove(tick); return; }
        f++;
        if (f <= 5) {
          t.scale.set(1.3 - f * 0.06);
        }
        // Fade out quickly if replaced, or normally after frame 20
        if (!(t as any)._isCombo) f = Math.max(f, 30);
        t.alpha = Math.max(0, 1 - (f - 20) / 15);
        if (f >= 35) {
          (t as any)._destroyed = true;
          particleContainer.removeChild(t);
          t.destroy();
          app.ticker.remove(tick);
        }
      };
      app.ticker.add(tick);
    },

    updateGlow(cps: number) {
      if (!app) return;
      const r = 70 * scale;
      let glowColor = 0xf0b90b;
      let glowAlpha = 0.12;
      if (cps >= 1e12) { glowColor = 0xff00ff; glowAlpha = 0.25; }
      else if (cps >= 1e9) { glowColor = 0x00e5ff; glowAlpha = 0.22; }
      else if (cps >= 1e6) { glowColor = 0x00c853; glowAlpha = 0.2; }
      else if (cps >= 1e3) { glowColor = 0xf0b90b; glowAlpha = 0.18; }
      drawGlow(r, glowColor, glowAlpha);
    },

    getCoinCenter() {
      if (!app) return { x: window.innerWidth / 2, y: window.innerHeight * 0.35, r: 70 };
      const r = 70 * scale;
      return {
        x: dogeContainer.position.x,
        y: dogeContainer.position.y,
        r: r * 2.5,
      };
    },

    resize() {
      if (!app) return;
      app.renderer.resize(window.innerWidth, window.innerHeight);
      calcScale();
      positionDoge();
      app.stage.hitArea = new PIXI.Rectangle(0, 0, window.innerWidth, window.innerHeight);
      const r = 70 * scale;
      drawGlow(r, 0xf0b90b, 0.12);
      const coinSize = r * 2;
      dogeSprite.width = coinSize;
      dogeSprite.height = coinSize;
    },

    destroy() {
      if (app) app.destroy(true);
    },
  };
}
